import { BRAND } from '../lib/brand';

export interface PlaceholderContext {
  customerName?: string;
  contactName?: string;
  contactEmail?: string;
  courseName?: string;
  /** Language of delivery for course sessions (e.g. English) — maps to {{CourseLanguage}} */
  courseLanguage?: string;
  courseDate?: string;
  startTime?: string;
  endTime?: string;
  attendeeCount?: number;
  locationAddress?: string;
  instructorName?: string;
  notes?: string;
  bookings?: any[];
  // Contact information
  contactFirstName?: string;
  contactLastName?: string;
  contactPhoneNumber?: string;
  // Company information
  companyName?: string;
  companyEmail?: string;
  companyPhone?: string;
  companyLandline?: string;
  companyWebsite?: string;
  // Company address information
  companyAddressLine1?: string;
  companyAddressLine2?: string;
  companyAddressLine3?: string;
  companyCity?: string;
  companyCountyState?: string;
  companyEircodePostcode?: string;
  companyCountry?: string;
  // Location information
  location?: string;
  // Location contact information
  locationContactName?: string;
  locationContactPhone?: string;
  locationContactEmail?: string;
  // Instructor-specific information
  instructorPrice?: string;
  instructorPO?: string;
  allDatesList?: string;
  // Booking information
  schedule?: string;
  totalPrice?: string;
  clientPO?: string;
  // Time-based greeting
  timeBasedGreeting?: string;
  // Logo URL
  logoUrl?: string;
  // Multi-day course dates formatted
  courseDatesFormatted?: string;
  /** Stable reference codes for templates (CRM / sessions) */
  courseSessionReference?: string;
  bookingReference?: string;
  customerId?: string;
  /** Venue eircode / postcode (Admin → Location) */
  locationEircode?: string;
  /** Venue notes (Admin → Location Notes) — not booking notes */
  locationNotes?: string;
  /** Shared HTML block from course email wording assignment */
  courseEmailContent?: string;
  // Test link email placeholders
  testLink?: string;
  attendeesList?: string;
  [key: string]: any;
}

export class PlaceholderService {
  private static readonly PLACEHOLDER_PATTERNS = {
    CUSTOMER_NAME: /\{\{CustomerName\}\}/g,
    CONTACT_NAME: /\{\{ContactName\}\}/g,
    CONTACT_EMAIL: /\{\{ContactEmail\}\}/g,
    COURSE_NAME: /\{\{CourseName\}\}/g,
    COURSE_DATE: /\{\{CourseDate\}\}/g,
    START_TIME: /\{\{StartTime\}\}/g,
    END_TIME: /\{\{EndTime\}\}/g,
    ATTENDEE_COUNT: /\{\{AttendeeCount\}\}/g,
    LOCATION_ADDRESS: /\{\{LocationAddress\}\}/g,
    INSTRUCTOR_NAME: /\{\{InstructorName\}\}/g,
    NOTES: /\{\{Notes\}\}/g,
    COURSE_DETAILS_LIST: /\{\{CourseDetailsList\}\}/g,
    COURSE_DETAILS_LIST_HTML: /\{\{CourseDetailsListHTML\}\}/g,
    COURSE_DETAILS_LIST_TEXT: /\{\{CourseDetailsListText\}\}/g,
    // Contact placeholders
    CONTACT_FIRST_NAME: /\{\{ContactFirstName\}\}/g,
    CONTACT_LAST_NAME: /\{\{ContactLastName\}\}/g,
    CONTACT_PHONE_NUMBER: /\{\{ContactPhoneNumber\}\}/g,
    // Company placeholders
    COMPANY_NAME: /\{\{CompanyName\}\}/g,
    COMPANY_EMAIL: /\{\{CompanyEmail\}\}/g,
    COMPANY_PHONE: /\{\{CompanyPhone\}\}/g,
    COMPANY_LANDLINE: /\{\{CompanyLandline\}\}/g,
    COMPANY_WEBSITE: /\{\{CompanyWebsite\}\}/g,
    // Company address placeholders
    COMPANY_ADDRESS_LINE_1: /\{\{CompanyAddressLine1\}\}/g,
    COMPANY_ADDRESS_LINE_2: /\{\{CompanyAddressLine2\}\}/g,
    COMPANY_ADDRESS_LINE_3: /\{\{CompanyAddressLine3\}\}/g,
    COMPANY_CITY: /\{\{CompanyCity\}\}/g,
    COMPANY_COUNTY_STATE: /\{\{CompanyCountyState\}\}/g,
    COMPANY_EIRCODE_POSTCODE: /\{\{CompanyEircodePostcode\}\}/g,
    COMPANY_COUNTRY: /\{\{CompanyCountry\}\}/g,
    // Location placeholder
    LOCATION: /\{\{Location\}\}/g,
    // Location contact placeholders
    LOCATION_CONTACT_NAME: /\{\{LocationContactName\}\}/g,
    LOCATION_CONTACT_PHONE: /\{\{LocationContactPhone\}\}/g,
    LOCATION_CONTACT_EMAIL: /\{\{LocationContactEmail\}\}/g,
    // Instructor-specific placeholders
    INSTRUCTOR_PRICE: /\{\{InstructorPrice\}\}/g,
    INSTRUCTOR_PO: /\{\{InstructorPO\}\}/g,
    ALL_DATES_LIST: /\{\{AllDatesList\}\}/g,
    // Booking placeholders
    SCHEDULE: /\{\{Schedule\}\}/g,
    TOTAL_PRICE: /\{\{TotalPrice\}\}/g,
    CLIENT_PO: /\{\{ClientPO\}\}/g,
    // Time-based greeting
    TIME_BASED_GREETING: /\{\{TimeBasedGreeting\}\}/g,
    // Logo URL
    LOGO_URL: /\{\{logoUrl\}\}/g,
    // Multi-day course dates
    COURSE_DATES_FORMATTED: /\{\{CourseDatesFormatted\}\}/g,
    // Date options for quote emails
    DATE_OPTION_1: /\{\{DateOption1\}\}/g,
    DATE_OPTION_2: /\{\{DateOption2\}\}/g,
    DATE_OPTION_3: /\{\{DateOption3\}\}/g,
    DATE_OPTION_4: /\{\{DateOption4\}\}/g,
    DATE_OPTION_5: /\{\{DateOption5\}\}/g,
    // Combined selected dates (only shows selected dates)
    SELECTED_DATES: /\{\{SelectedDates\}\}/g,
    SELECTED_DATES_LIST: /\{\{SelectedDatesList\}\}/g,
    SELECTED_DATES_BULLETS: /\{\{SelectedDatesBullets\}\}/g,
    // Location or onsite
    LOCATION_OR_ONSITE: /\{\{LocationOrOnsite\}\}/g,
    // Test link email
    TEST_LINK: /\{\{TestLink\}\}/g,
    ATTENDEES_LIST: /\{\{AttendeesList\}\}/g,
    PARTICIPANT_LIST: /\{\{ParticipantList\}\}/g,
    PARTICIPANT_COUNT: /\{\{ParticipantCount\}\}/g,
  };

  public static generatePlaceholders(context: PlaceholderContext): Record<string, string> {
    // Extract data from objects if provided
    const customer = (context as any).customer;
    const contact = (context as any).contact;
    const instructor = (context as any).instructor;
    const location = (context as any).location;
    const bookingData = (context as any).bookingData;
    const quoteData = (context as any).quoteData;
    const emailType = (context as any).emailType; // Add support for email type

    const placeholders: Record<string, string> = {
      '{{CustomerName}}': context.customerName || customer?.companyName || (customer as any)?.company_name || '',
      '{{ContactName}}': context.contactName || (contact ? `${contact.firstName || (contact as any)?.first_name || ''} ${contact.lastName || (contact as any)?.last_name || ''}`.trim() : '') || '',
      '{{ContactEmail}}': context.contactEmail || contact?.email || '',
      '{{CourseName}}': context.courseName || bookingData?.courseName || '',
      '{{CourseLanguage}}':
        String(context.courseLanguage ?? bookingData?.courseLanguage ?? '').trim() ||
        String(bookingData?.language ?? '').trim() ||
        '',
      '{{Language}}':
        String(context.courseLanguage ?? bookingData?.courseLanguage ?? '').trim() ||
        String(bookingData?.language ?? '').trim() ||
        '',
      '{{CourseSessionReference}}': context.courseSessionReference || bookingData?.courseSessionReference || '',
      '{{BookingReference}}': context.bookingReference || bookingData?.bookingReference || '',
      '{{ReferenceNumber}}':
        context.bookingReference ||
        bookingData?.bookingReference ||
        context.courseSessionReference ||
        bookingData?.courseSessionReference ||
        '',
      '{{CustomerReferenceId}}': context.customerId || bookingData?.customerId || (customer as any)?.id || '',
      '{{CourseDate}}': context.courseDate || bookingData?.courseDate || '',
      '{{StartTime}}': context.startTime || bookingData?.startTime || '',
      '{{EndTime}}': context.endTime || bookingData?.endTime || '',
      '{{CourseTimeRange}}': (() => {
        const s = String(context.startTime || bookingData?.startTime || '').trim();
        const e = String(context.endTime || bookingData?.endTime || '').trim();
        if (s && e) return `${s} - ${e}`;
        return s || e;
      })(),
      '{{AttendeeCount}}': context.attendeeCount?.toString() || bookingData?.attendeeCount || '1',
      '{{Attendees}}': (() => {
        const attendees = bookingData?.attendees || (context as any).attendees;
        return this.formatAttendees(attendees);
      })(),
      '{{LocationAddress}}': this.resolveLocationAddressLine(context, location),
      '{{LocationEircode}}':
        context.locationEircode ||
        this.resolveLocationEircode(location) ||
        this.extractEircodeFromText(
          this.formatLocationAddress(location) ||
            (typeof context.location === 'string' ? context.location : '') ||
            context.locationAddress ||
            ''
        ) ||
        '',
      '{{LocationNotes}}':
        context.locationNotes || this.resolveLocationNotes(location) || '',
      '{{InstructorName}}': context.instructorName || instructor?.firstName || '', // First name only
      '{{Notes}}': context.notes || bookingData?.notes || '',
      // Instructor-specific placeholders
      '{{InstructorFirstName}}': context.instructorFirstName || instructor?.firstName || '',
      '{{InstructorLastName}}': context.instructorLastName || instructor?.lastName || '',
      '{{InstructorEmail}}': context.instructorEmail || instructor?.email || '',
      // Contact placeholders - support both camelCase and snake_case
      '{{ContactFirstName}}': context.contactFirstName || contact?.firstName || (contact as any)?.first_name || '',
      '{{ContactLastName}}': context.contactLastName || contact?.lastName || (contact as any)?.last_name || '',
      '{{ContactPhoneNumber}}': context.contactPhoneNumber || contact?.phoneNumber || (contact as any)?.phone_number || '',
      // Company placeholders - CompanyName should match CustomerName for instructor emails
      // Support both camelCase and snake_case
      '{{CompanyName}}': emailType === 'instructor' 
        ? (context.customerName || customer?.companyName || (customer as any)?.company_name || '') 
        : (context.companyName || customer?.companyName || (customer as any)?.company_name || ''),
      '{{CompanyEmail}}': context.companyEmail || customer?.companyEmail || BRAND.email,
      '{{CompanyPhone}}': context.companyPhone || customer?.companyPhone || BRAND.phoneMobile,
      '{{CompanyLandline}}': context.companyLandline || customer?.companyLandline || BRAND.phonePrimary,
      '{{CompanyWebsite}}': context.companyWebsite || customer?.companyWebsite || BRAND.websiteHost,
      // Company address placeholders
      '{{CompanyAddressLine1}}': context.companyAddressLine1 || customer?.addressLine1 || location?.addressLine1 || '',
      '{{CompanyAddressLine2}}': context.companyAddressLine2 || customer?.addressLine2 || location?.addressLine2 || '',
      '{{CompanyAddressLine3}}': context.companyAddressLine3 || customer?.addressLine3 || location?.addressLine3 || '',
      '{{CompanyCity}}': context.companyCity || customer?.city || location?.city || '',
      '{{CompanyCountyState}}': context.companyCountyState || customer?.countyState || location?.countyState || '',
      '{{CompanyEircodePostcode}}':
        context.companyEircodePostcode ||
        customer?.eircodePostcode ||
        this.resolveLocationEircode(location) ||
        '',
      '{{CompanyCountry}}': context.companyCountry || customer?.country || location?.country || '',
      // Location placeholder
      '{{Location}}':
        this.formatLocationAddress(location) ||
        (typeof location === 'object' && location ? String(location.name || '').trim() : '') ||
        (typeof context.location === 'string' ? context.location.trim() : '') ||
        String(context.locationAddress || '').trim() ||
        '',
      // Location contact placeholders - for instructor emails, prioritize location contact; for customer emails, prioritize booking contact
      '{{LocationContactName}}': context.locationContactName ||
        (emailType === 'instructor'
          ? (location?.locationContactName || location?.contact_name || (contact ? `${contact.firstName} ${contact.lastName}`.trim() : ''))
          : ((contact ? `${contact.firstName} ${contact.lastName}`.trim() : '') || location?.locationContactName || location?.contact_name || '')),
      '{{LocationContactPhone}}': context.locationContactPhone ||
        (emailType === 'instructor'
          ? (location?.locationContactPhone || location?.contact_phone || contact?.phoneNumber || '')
          : (contact?.phoneNumber || location?.locationContactPhone || location?.contact_phone || '')),
      '{{LocationContactEmail}}': context.locationContactEmail ||
        (emailType === 'instructor'
          ? (location?.locationContactEmail || location?.contact_email || contact?.email || '')
          : (contact?.email || location?.locationContactEmail || location?.contact_email || '')),
      // Instructor-specific placeholders
      '{{InstructorPrice}}': this.formatPrice(context.instructorPrice || bookingData?.instructorPrice),
      '{{InstructorPO}}': context.instructorPO || bookingData?.instructorPO || '',
      '{{AllDatesList}}': context.allDatesList || this.formatAllDatesList(bookingData) || '',
      // Booking placeholders
      '{{Schedule}}': context.schedule || this.formatSchedule(bookingData) || '',
      '{{TotalPrice}}': this.formatPrice(context.totalPrice || bookingData?.totalPrice || bookingData?.price),
      '{{CourseFee}}':
        context.courseFee ||
        this.formatPrice(bookingData?.enrollmentCourseFee || bookingData?.totalPrice || bookingData?.price),
      '{{AmountPaid}}': context.amountPaid || this.formatPrice(bookingData?.amountPaid),
      '{{BalanceDue}}': context.balanceDue || '',
      '{{PaymentStatus}}': String(context.paymentStatus || bookingData?.paymentStatus || '').trim(),
      '{{ClientPO}}': context.clientPO || bookingData?.clientPO || bookingData?.poNumber || '',
      // Time-based greeting - support both camelCase and snake_case
      '{{TimeBasedGreeting}}': context.timeBasedGreeting || this.getTimeBasedGreeting(contact?.firstName || (contact as any)?.first_name || ''),
      // Logo URL - use absolute URL for email compatibility
      '{{logoUrl}}': context.logoUrl || '',
      // Signature - use value from context only (loaded from system settings per tenant)
      '{{signatureUrl}}': context.signatureUrl || '',
      // Also provide just the URL for manual image tags
      '{{signatureImageUrl}}': (context as any).signatureImageUrl || '',
      // Multi-line location address for instructor assignment emails
      '{{LocationAddressMultiline}}':
        this.formatLocationAddressMultiline(location) ||
        (typeof context.location === 'string' ? context.location : '') ||
        '',
      '{{LocationMapsUrl}}': String((context as any).locationMapsUrl || (context as any).locationMapsLink || '').trim(),
      '{{LocationMapsLink}}': String((context as any).locationMapsLink || (context as any).locationMapsUrl || '').trim(),
      // QR code link
      '{{QRCodeLink}}': `https://${BRAND.websiteHost}/qr`,
      // Formatted date in DD/MM/YYYY format
      '{{CourseDateFormatted}}': bookingData?.courseDate ? this.formatDate(bookingData.courseDate) : (context.courseDate ? this.formatDate(context.courseDate) : ''),
      // Formatted start time with am/pm
      '{{StartTimeFormatted}}': bookingData?.startTime ? this.formatTimeWithAmPm(bookingData.startTime) : (context.startTime ? this.formatTimeWithAmPm(context.startTime) : 'TBD'),
      // Quote-specific placeholders
      '{{QuoteNumber}}': (context as any).quoteData?.quoteNumber || (context as any).quoteNumber || '',
      '{{QuoteAcceptanceLink}}': (context as any).quoteData?.acceptanceLink || (context as any).acceptanceLink || '',
      '{{QuoteValidUntil}}': (context as any).quoteData?.validUntil ? this.formatDate((context as any).quoteData.validUntil) : ((context as any).validUntil ? this.formatDate((context as any).validUntil) : ''),
      // Date options for quote emails (from selected dates)
      '{{DateOption1}}': '',
      '{{DateOption2}}': '',
      '{{DateOption3}}': '',
      '{{DateOption4}}': '',
      '{{DateOption5}}': '',
      '{{SelectedDates}}': '', // Combined placeholder - only shows selected dates
      '{{SelectedDatesList}}': '', // Numbered list format
      '{{SelectedDatesBullets}}': '', // Bullet point format
      // Location or onsite (from quote location or "Onsite" if no location)
      '{{LocationOrOnsite}}': (() => {
        const quoteLocation = (context as any).quoteData?.location || location;
        if (quoteLocation) {
          return this.formatLocationAddress(quoteLocation);
        }
        return 'Onsite';
      })(),
      // Test link email placeholders
      '{{TestLink}}': context.testLink || (context as any).testLinkUrl || '',
      '{{AttendeesList}}': context.attendeesList || (context as any).attendeesListFormatted || '',
      '{{ParticipantList}}':
        context.participantList ||
        (context as any).participantList ||
        context.attendeesList ||
        (context as any).attendeesListFormatted ||
        '',
      '{{ParticipantCount}}':
        context.participantCount != null
          ? String(context.participantCount)
          : (context as any).participantCount != null
          ? String((context as any).participantCount)
          : context.attendeeCount?.toString() || bookingData?.attendeeCount || '',
      '{{CourseEmailContent}}': context.courseEmailContent || '',
    };

      // Handle course details list placeholders
    if (context.bookings && context.bookings.length > 0) {
      placeholders['{{CourseDetailsList}}'] = this.formatCourseDetailsListText(context.bookings);
      placeholders['{{CourseDetailsListHTML}}'] = this.formatCourseDetailsListHTML(context.bookings);
      placeholders['{{CourseDetailsListText}}'] = this.formatCourseDetailsListText(context.bookings);
    } else if (bookingData) {
      // For single booking data, create a simple course details text
      // Treat single booking as array for consistent formatting
      const singleBookingArray = [bookingData];
      placeholders['{{CourseDetailsList}}'] = this.formatCourseDetailsListText(singleBookingArray);
      placeholders['{{CourseDetailsListHTML}}'] = this.formatCourseDetailsListHTML(singleBookingArray);
      // For instructor emails, show dates with times since each date might have different times
      placeholders['{{CourseDetailsListText}}'] = this.formatDatesWithTimesText(singleBookingArray);
    } else {
      placeholders['{{CourseDetailsList}}'] = '';
      placeholders['{{CourseDetailsListHTML}}'] = '';
      placeholders['{{CourseDetailsListText}}'] = '';
    }

    // Add missing placeholders that are used in templates but not generated
    placeholders['{{CompanyCounty}}'] = placeholders['{{CompanyCountyState}}'];
    placeholders['{{CompanyEircode}}'] = placeholders['{{CompanyEircodePostcode}}'];
    placeholders['{{Language}}'] = placeholders['{{CourseLanguage}}'];
    placeholders['{{ReferenceNumber}}'] =
      placeholders['{{ReferenceNumber}}'] ||
      placeholders['{{BookingReference}}'] ||
      placeholders['{{CourseSessionReference}}'];

    // Calculate next upcoming date (from today forward)
    const nextDate = this.getNextUpcomingDate(bookingData);
    const multiDayDates =
      bookingData?.allBookingDates &&
      bookingData.allBookingDates.length > 1
        ? [...new Set(bookingData.allBookingDates.filter(Boolean) as string[])].sort((a, b) =>
            a.localeCompare(b),
          )
        : null;
    if (multiDayDates && multiDayDates.length > 1) {
      const allDatesText = multiDayDates.map((d) => this.formatDate(d)).join(', ');
      placeholders['{{CourseDate}}'] = allDatesText;
      placeholders['{{CourseDateFormatted}}'] = allDatesText;
      placeholders['{{NextUpcomingDate}}'] = nextDate ? this.formatDate(nextDate) : allDatesText;
    } else {
      placeholders['{{NextUpcomingDate}}'] = nextDate ? this.formatDate(nextDate) : placeholders['{{CourseDate}}'];
      placeholders['{{CourseDate}}'] = nextDate ? this.formatDate(nextDate) : placeholders['{{CourseDate}}'];
    }

    // Add AllBookingDates placeholder (chronological order, deduped)
    if (bookingData?.allBookingDates && bookingData.allBookingDates.length > 0) {
      const datesOrdered = [...new Set(bookingData.allBookingDates.filter(Boolean) as string[])].sort((a, b) =>
        a.localeCompare(b),
      );
      placeholders['{{AllBookingDates}}'] = datesOrdered
        .map((date: string) => {
          const formattedDate = this.formatDate(date);
          const formattedTime = this.formatTimeWithAmPm(bookingData.startTime || 'TBD');
          return `• <strong style="color: rgb(51, 103, 209); text-decoration: underline;">${formattedDate}</strong> <strong>Start Time:</strong> <span style="color: rgb(51, 103, 209); text-decoration: underline;">${formattedTime}</span>`;
        })
        .join('<br>');

      // Add a dates-only version for templates that already show course name
      placeholders['{{AllDatesOnly}}'] = datesOrdered
        .map((date: string) => {
          const formattedDate = this.formatDate(date);
          const formattedTime = this.formatTimeWithAmPm(bookingData.startTime || 'TBD');
          return `• <strong style="color: rgb(51, 103, 209); text-decoration: underline;">${formattedDate}</strong> <strong>Start Time:</strong> <span style="color: rgb(51, 103, 209); text-decoration: underline;">${formattedTime}</span>`;
        })
        .join('<br>');


      // Add multi-day formatted dates with times (Day 1: MM/DD/YYYY, Start Time: HH:MM)
      if (datesOrdered.length > 1) {
        // Use allBookingDatesWithTimes if available for individual times per day
        if (bookingData.allBookingDatesWithTimes && bookingData.allBookingDatesWithTimes.length > 0) {
          const byDate = new Map<string, { date?: string; startTime?: string }>(
            bookingData.allBookingDatesWithTimes.map((e: { date?: string; startTime?: string }) => [
              e.date as string,
              e,
            ]),
          );
          placeholders['{{CourseDatesFormatted}}'] = datesOrdered
            .map((dateStr: string, index: number) => {
              const dateEntry =
                byDate.get(dateStr) || { date: dateStr, startTime: bookingData.startTime || '09:00' };
              const formattedDate = this.formatDate(dateEntry.date || dateStr);
              const formattedTime = this.formatTimeWithAmPm(dateEntry.startTime || '09:00');
              return `<strong>Day ${index + 1}:</strong> ${formattedDate}<br><strong>Start Time:</strong> ${formattedTime}`;
            })
            .join('<br><br>');
        } else {
          // Fallback to allBookingDates with single start time
          placeholders['{{CourseDatesFormatted}}'] = datesOrdered
            .map((date: string, index: number) => {
              const formattedDate = this.formatDate(date);
              const formattedTime = this.formatTimeWithAmPm(bookingData.startTime || '09:00');
              return `<strong>Day ${index + 1}:</strong> ${formattedDate}<br><strong>Start Time:</strong> ${formattedTime}`;
            })
            .join('<br><br>');
        }
      } else {
        // Single day course - just show the date and time
        const formattedDate = this.formatDate(datesOrdered[0]!);
        const formattedTime = this.formatTimeWithAmPm(bookingData.startTime || '09:00');
        placeholders['{{CourseDatesFormatted}}'] = `<strong>Date:</strong> ${formattedDate}<br><strong>Start Time:</strong> ${formattedTime}`;
      }
    } else if (bookingData?.courseDate) {
      const formattedDate = this.formatDate(bookingData.courseDate);
      const formattedTime = this.formatTimeWithAmPm(bookingData.startTime || 'TBD');
      const dateTimeString = `• <strong style="color: rgb(51, 103, 209); text-decoration: underline;">${formattedDate}</strong> <strong>Start Time:</strong> <span style="color: rgb(51, 103, 209); text-decoration: underline;">${formattedTime}</span>`;
      placeholders['{{AllBookingDates}}'] = dateTimeString;
      placeholders['{{AllDatesOnly}}'] = dateTimeString;
      placeholders['{{CourseDatesFormatted}}'] = `<strong>Date:</strong> ${formattedDate}`;
    } else {
      placeholders['{{AllBookingDates}}'] = '';
      placeholders['{{AllDatesOnly}}'] = '';
      placeholders['{{CourseDatesFormatted}}'] = '';
    }
    
    // Rich HTML variant for NextUpcomingDate (single calendar day only — multi-day uses plain list on {{CourseDate}})
    if (placeholders['{{CourseDate}}'] && !(multiDayDates && multiDayDates.length > 1)) {
      const formattedDate = this.formatDate(placeholders['{{CourseDate}}']);
      const formattedTime = this.formatTimeWithAmPm(bookingData?.startTime || 'TBD');
      placeholders['{{NextUpcomingDate}}'] = `• <strong style="color: rgb(51, 103, 209); text-decoration: underline;">${formattedDate}</strong> <strong>Start Time:</strong> <span style="color: rgb(51, 103, 209); text-decoration: underline;">${formattedTime}</span>`;
    }
    
    // Handle selected dates for DateOption placeholders (up to 5 dates)
    const selectedDates = (context as any).quoteData?.selectedDates || [];
    if (selectedDates && selectedDates.length > 0) {
      selectedDates.slice(0, 5).forEach((date: string, index: number) => {
        const formattedDate = this.formatDate(date);
        placeholders[`{{DateOption${index + 1}}}`] = formattedDate;
      });
      
      // Create a combined placeholder that only shows selected dates (separated by <br>)
      const formattedDates = selectedDates.slice(0, 5).map((date: string) => this.formatDate(date));
      placeholders['{{SelectedDates}}'] = formattedDates.join('<br>');
      placeholders['{{SelectedDatesList}}'] = formattedDates.map((d: string, i: number) => `${i + 1}. ${d}`).join('<br>');
      placeholders['{{SelectedDatesBullets}}'] = formattedDates.map((d: string) => `&bull; ${d}`).join('<br>');
    } else {
      // If no dates selected, provide empty combined placeholder
      placeholders['{{SelectedDates}}'] = '';
      placeholders['{{SelectedDatesList}}'] = '';
    }
    
    // Ensure all placeholders have values (replace undefined with empty string)
    Object.keys(placeholders).forEach(key => {
      if (placeholders[key] === undefined || placeholders[key] === null) {
        placeholders[key] = '';
      }
      // Format dates that might still be in YYYY-MM-DD format
      if (key.includes('Date') && placeholders[key] && placeholders[key].match(/^\d{4}-\d{2}-\d{2}$/)) {
        placeholders[key] = this.formatDate(placeholders[key]);
      }
    });

    return placeholders;
  }

  public static replacePlaceholders(text: string, context: PlaceholderContext): string {
    // If context is already a placeholders object (from generatePlaceholders), use it directly
    const placeholders = typeof context === 'object' && '{{ContactFirstName}}' in context 
      ? context as Record<string, string>
      : this.generatePlaceholders(context);
    
    let result = this.normalizeTemplatePlaceholderTokens(text);
    result = this.applyPlaceholderMap(result, placeholders);
    // Wording blocks may themselves contain tokens; resolve them after insert.
    result = this.applyPlaceholderMap(result, placeholders, new Set(['{{CourseEmailContent}}']));

    const bookingRef =
      placeholders['{{BookingReference}}'] ||
      placeholders['{{CourseSessionReference}}'] ||
      placeholders['{{ReferenceNumber}}'] ||
      '';
    if (bookingRef) {
      result = result.replace(/\(reference number placeholder here\)/gi, bookingRef);
    }

    // Templates often include a € sign while fee placeholders already include one.
    result = result.replace(/€(?:\s|&nbsp;)*€/gi, '€');

    return result;
  }

  private static applyPlaceholderMap(
    text: string,
    placeholders: Record<string, string>,
    skipKeys?: Set<string>,
  ): string {
    let result = text;
    Object.entries(placeholders).forEach(([placeholder, value]) => {
      if (skipKeys?.has(placeholder)) return;
      const replacement = value || '';
      if (replacement.includes(placeholder)) {
        result = result.split(placeholder).join(replacement);
        return;
      }
      while (result.includes(placeholder)) {
        result = result.replace(placeholder, replacement);
      }
    });
    return result;
  }

  /** Contenteditable can wrap letters inside {{tokens}} with spans / nbsp. */
  private static normalizeTemplatePlaceholderTokens(text: string): string {
    if (!text) return text;
    const result = text
      .replace(/&#123;/g, '{')
      .replace(/&#125;/g, '}');
    return result.replace(/\{\{([\s\S]*?)\}\}/g, (_match, inner: string) => {
      const cleaned = String(inner)
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&#160;/g, ' ')
        .replace(/\u00a0/g, ' ')
        .replace(/\s+/g, '')
        .trim();
      return `{{${cleaned}}}`;
    });
  }

  private static formatDate(dateStr: string): string {
    try {
      const date = new Date(dateStr);
      // Format as DD/MM/YYYY (European format)
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return dateStr;
    }
  }

  private static formatTimeWithAmPm(timeStr: string): string {
    if (!timeStr) return 'TBD';
    
    try {
      const [hours, minutes] = timeStr.split(':');
      const hour24 = parseInt(hours, 10);
      const min = parseInt(minutes, 10);
      
      if (isNaN(hour24) || isNaN(min)) return timeStr;
      
      const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
      const ampm = hour24 >= 12 ? 'pm' : 'am';
      const formattedMinutes = min.toString().padStart(2, '0');
      
      return `${hour12.toString().padStart(2, '0')}:${formattedMinutes}${ampm}`;
    } catch {
      return timeStr;
    }
  }

  private static getTimeBasedGreeting(contactName: string): string {
    const name = (contactName || '').trim();
    const suffix = name ? ` ${name}` : '';
    // Get current time in Irish timezone (Europe/Dublin)
    const now = new Date();
    const irishTime = new Date(now.toLocaleString("en-US", {timeZone: "Europe/Dublin"}));
    const hour = irishTime.getHours();
    // Morning: 00:00 - 11:59 | Afternoon: 12:00 - 17:59 | Evening: 18:00+
    if (hour < 12) return `Good Morning${suffix}`;
    if (hour < 18) return `Good Afternoon${suffix}`;
    return `Good Evening${suffix}`;
  }

  private static formatCourseDetailsListHTML(bookings: any[]): string {
    const courseDetails = bookings.map(booking => {
      const formattedDate = this.formatDate(booking.courseDate || booking.date);
      const formattedTime = this.formatTimeWithAmPm(booking.startTime || 'TBD');
      const courseName = booking.courseName || 'Course';
      
      return `<li style="margin-bottom: 10px; color: #374151;">
        <strong style="color: #099955;">${courseName}</strong><br>
        ${formattedDate} Start Time: ${formattedTime}
      </li>`;
    });
    
    return `<ul style="list-style-type: disc; padding-left: 20px; margin: 0;">${courseDetails.join('')}</ul>`;
  }

  private static formatCourseDetailsListText(bookings: any[]): string {
    const courseDetails = bookings.map(booking => {
      const formattedDate = this.formatDate(booking.courseDate || booking.date);
      const formattedTime = this.formatTimeWithAmPm(booking.startTime || 'TBD');
      const courseName = booking.courseName || 'Course';
      
      return `• ${courseName}\n  ${formattedDate} Start Time: ${formattedTime}`;
    });
    
    return courseDetails.join('\n\n');
  }

  private static formatDatesOnlyText(bookings: any[]): string {
    return bookings.map(booking => {
      const formattedDate = this.formatDate(booking.courseDate || booking.date);
      const formattedTime = this.formatTimeWithAmPm(booking.startTime || 'TBD');
      return `${formattedDate} Start Time: ${formattedTime}`;
    }).join(', ');
  }

  private static formatDatesWithTimesText(bookings: any[]): string {
    return bookings.map(booking => {
      const formattedDate = this.formatDate(booking.courseDate || booking.date);
      const formattedTime = this.formatTimeWithAmPm(booking.startTime || 'TBD');
      return `• ${formattedDate} Start Time: ${formattedTime}`;
    }).join(', ');
  }

  /** Eircode from CRM (`eircode_postcode`) or training-location (`postcode`) columns. */
  private static resolveLocationEircode(location: any): string {
    if (!location || typeof location === 'string') return '';
    let loc = location;
    if (loc.location && typeof loc.location === 'object') loc = loc.location;
    return String(
      loc.eircodePostcode || loc.eircode_postcode || loc.postcode || ''
    ).trim();
  }

  private static extractEircodeFromText(text: string): string {
    const match = String(text || '').toUpperCase().match(/\b([A-Z]\d{2}\s?[A-Z0-9]{4})\b/);
    return match ? match[1].replace(/\s+/g, ' ').trim() : '';
  }

  /** Venue notes from CRM (`location_notes`) or training-location (`notes`) columns. */
  private static resolveLocationNotes(location: any): string {
    if (!location || typeof location === 'string') return '';
    let loc = location;
    if (loc.location && typeof loc.location === 'object') loc = loc.location;
    return String(loc.locationNotes || loc.location_notes || loc.notes || '').trim();
  }

  /** Street / line-1 address; falls back so {{LocationAddress}} is not blank. */
  private static resolveLocationAddressLine(context: PlaceholderContext, location: any): string {
    if (context.locationAddress) return String(context.locationAddress).trim();
    if (location && typeof location === 'string') {
      const s = location.trim();
      // "Venue - Address, City" → prefer the address segment after " - "
      const dash = s.indexOf(' - ');
      if (dash > 0) return s.slice(dash + 3).trim() || s;
      return s;
    }
    if (!location || typeof location !== 'object') return '';
    let loc = location;
    if (loc.location && typeof loc.location === 'object') loc = loc.location;
    const line1 = String(
      loc.addressLine1 || loc.address_line_1 || loc.address || ''
    ).trim();
    if (line1) return line1;
    const formatted = this.formatLocationAddress(loc);
    return formatted;
  }

  private static formatLocationAddress(location: any): string {
    if (!location) return '';

    // Already-formatted strings (e.g. "Venue - Address line, City, Country")
    // were causing this function to walk object properties that do not exist
    // and return ''. Hand pre-formatted strings straight back.
    if (typeof location === 'string') return location.trim();

    let loc = location;
    if (typeof loc === 'object' && loc.location && typeof loc.location !== 'string') {
      loc = loc.location;
    }
    if (typeof loc === 'string') return loc.trim();
    if (typeof loc !== 'object') return '';

    const addressParts: string[] = [];

    if (loc.addressLine1 || loc.address_line_1 || loc.address) {
      addressParts.push(loc.addressLine1 || loc.address_line_1 || loc.address);
    }
    if (loc.addressLine2 || loc.address_line_2) {
      addressParts.push(loc.addressLine2 || loc.address_line_2);
    }
    if (loc.addressLine3 || loc.address_line_3) {
      addressParts.push(loc.addressLine3 || loc.address_line_3);
    }
    if (loc.city) addressParts.push(loc.city);
    if (loc.countyState || loc.county_state || loc.county) {
      addressParts.push(loc.countyState || loc.county_state || loc.county);
    }
    const eircode = this.resolveLocationEircode(loc);
    if (eircode) addressParts.push(eircode);
    if (loc.country) addressParts.push(loc.country);

    const filteredParts = addressParts.filter(
      (part) => part && String(part).trim() && part !== 'undefined'
    );

    const name = String(loc.name || loc.locationName || loc.location_name || '').trim();
    if (filteredParts.length === 0) {
      return name;
    }

    const joined = filteredParts.join(', ');
    if (name && !joined.toLowerCase().startsWith(name.toLowerCase())) {
      return `${name} - ${joined}`;
    }
    return joined;
  }

  private static formatLocationAddressMultiline(location: any): string {
    if (!location) return '';

    if (typeof location === 'string') {
      const s = location.trim();
      if (!s) return '';
      // Convert "A - B, C, D" style labels into readable lines when possible
      const dash = s.indexOf(' - ');
      if (dash > 0) {
        const name = s.slice(0, dash).trim();
        const rest = s.slice(dash + 3).trim();
        return [name, ...rest.split(',').map((p) => p.trim()).filter(Boolean)].join('\n');
      }
      return s.split(',').map((p) => p.trim()).filter(Boolean).join('\n') || s;
    }

    // Handle different location object structures
    let loc = location;

    // If location is nested, extract it
    if (location.location && typeof location.location === 'object') {
      loc = location.location;
    }
    if (typeof loc === 'string') {
      return this.formatLocationAddressMultiline(loc);
    }

    const addressLines: string[] = [];

    const name = String(loc.name || loc.locationName || loc.location_name || '').trim();
    if (name) addressLines.push(name);

    // Add address lines
    if (loc.addressLine1 || loc.address_line_1 || loc.address) {
      addressLines.push(loc.addressLine1 || loc.address_line_1 || loc.address);
    }
    if (loc.addressLine2 || loc.address_line_2) {
      addressLines.push(loc.addressLine2 || loc.address_line_2);
    }
    if (loc.addressLine3 || loc.address_line_3) {
      addressLines.push(loc.addressLine3 || loc.address_line_3);
    }

    // Add city, county, eircode on separate lines
    const cityCounty = [];
    if (loc.city) {
      cityCounty.push(loc.city);
    }
    if (loc.countyState || loc.county_state || loc.county) {
      cityCounty.push(loc.countyState || loc.county_state || loc.county);
    }
    if (cityCounty.length > 0) {
      addressLines.push(cityCounty.join(', '));
    }

    // Add eircode/postcode on separate line
    const eircode = this.resolveLocationEircode(loc);
    if (eircode) {
      addressLines.push(eircode);
    }

    // Add country on separate line if not Ireland
    if (loc.country && loc.country !== 'Ireland') {
      addressLines.push(loc.country);
    }

    return addressLines.filter(line => line && String(line).trim() && line !== 'undefined').join('\n');
  }

  private static formatAllDatesList(bookingData: any): string {
    if (!bookingData) return '';
    
    // If allBookingDates is available, use it
    if (bookingData.allBookingDates && bookingData.allBookingDates.length > 0) {
      return bookingData.allBookingDates.map((date: string) => this.formatDate(date)).join(', ');
    }
    
    // Otherwise, use the single courseDate
    if (bookingData.courseDate) {
      return this.formatDate(bookingData.courseDate);
    }
    
    return '';
  }

  private static formatSchedule(bookingData: any): string {
    if (!bookingData) return '';
    
    const parts = [];
    
    // Add course name if available
    if (bookingData.courseName) {
      parts.push(bookingData.courseName);
    }
    
    // Add date(s)
    if (bookingData.allBookingDates && bookingData.allBookingDates.length > 0) {
      const formattedDates = bookingData.allBookingDates.map((date: string) => {
        const formattedDate = this.formatDate(date);
        const formattedTime = this.formatTimeWithAmPm(bookingData.startTime || 'TBD');
        return `${formattedDate} Start Time: ${formattedTime}`;
      });
      parts.push(`Training Dates:\n${formattedDates.map(d => `• ${d}`).join('\n')}`);
    } else if (bookingData.courseDate) {
      const formattedDate = this.formatDate(bookingData.courseDate);
      const formattedTime = this.formatTimeWithAmPm(bookingData.startTime || 'TBD');
      parts.push(`Training Date: ${formattedDate} Start Time: ${formattedTime}`);
    }
    
    // Add attendee count if available
    if (bookingData.attendeeCount) {
      parts.push(`Attendees: ${bookingData.attendeeCount}`);
    }
    
    return parts.join('\n');
  }

  private static formatAttendees(attendees?: Array<{id: string; attendeeName: string}>): string {
    if (!attendees || attendees.length === 0) {
      return '';
    }
    
    return attendees.map(a => a.attendeeName).join(', ');
  }

  private static formatPrice(price: any): string {
    if (!price || price === '' || price === null || price === undefined) {
      return '';
    }
    
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice)) {
      return price.toString();
    }
    
    return `€${numPrice.toFixed(2)}`;
  }

  private static getNextUpcomingDate(bookingData: any): string | null {
    if (!bookingData) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Check if we have multiple booking dates
    if (bookingData.allBookingDates && bookingData.allBookingDates.length > 0) {
      // Filter dates that are today or in the future
      const upcomingDates = bookingData.allBookingDates
        .map((dateStr: string) => {
          const date = new Date(dateStr);
          return { dateStr, date };
        })
        .filter(({ date }) => date >= today)
        .sort((a, b) => a.date.getTime() - b.date.getTime());

      // Return the earliest upcoming date
      if (upcomingDates.length > 0) {
        return upcomingDates[0].dateStr;
      }

      // If no upcoming dates, return the last date (course already completed)
      return bookingData.allBookingDates[bookingData.allBookingDates.length - 1];
    }

    // If only single courseDate, return it
    if (bookingData.courseDate) {
      return bookingData.courseDate;
    }

    return null;
  }
}